/**
 * Pure geometry for the corner resize handles that replace the corner
 * rotation gesture on a selected construction line (issue #388).
 *
 * All math runs in the line's *unrotated* local frame (the frame
 * `getUnrotatedBounds` reports), so callers map the cursor into that frame
 * first. Resize is a "destructive reshape" like a vertex drag, so the caller
 * clears any baked rotation afterwards — these helpers only deal with the
 * axis-aligned box.
 *
 * `resizePathDataByBox` is the one paper-touching helper here (it takes the
 * scope explicitly, so the module stays runtime-paper-free): it applies a
 * box-to-box affine to a raw pathData string. It's shared by construction's
 * generic resize branch and the leather transform controller (#971) so the two
 * can't drift.
 */

import type paper from "paper";

export type ResizeCorner = "tl" | "tr" | "bl" | "br";
/** Edge (side) resize handles (#863): top / right / bottom / left. Dragging one
 *  resizes a SINGLE dimension — the opposite edge stays pinned (or both move
 *  symmetrically under from-centre). */
export type ResizeEdge = "t" | "r" | "b" | "l";
export type ResizeHandle = ResizeCorner | ResizeEdge;

export function isResizeEdge(h: ResizeHandle): h is ResizeEdge {
  return h === "t" || h === "r" || h === "b" || h === "l";
}

/** Whether a shape offers EDGE (single-dimension) handles (#863). Rect /
 *  ellipse / generic paths do; a `line` or `pill` is excluded — a one-axis
 *  scale would shear the line's slope or skew the capsule, so those keep only
 *  their corner (aspect-locked) handles. */
export function allowsEdgeResize(primKind: string | undefined): boolean {
  return primKind !== "line" && primKind !== "pill";
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Midpoint of each edge of `box`, keyed by edge — the anchor point for the
 *  edge handle squares (render + hit-test). */
export function edgeMidpoints(
  box: Box,
): Record<ResizeEdge, { x: number; y: number }> {
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  return {
    t: { x: cx, y: box.y },
    b: { x: cx, y: box.y + box.height },
    l: { x: box.x, y: cy },
    r: { x: box.x + box.width, y: cy },
  };
}

/** The corner diagonally opposite `corner` — the one that stays pinned while
 *  the grabbed corner is dragged (unless resizing from the centre). */
export function oppositeCornerPoint(
  box: Box,
  corner: ResizeCorner,
): { x: number; y: number } {
  const left = box.x;
  const right = box.x + box.width;
  const top = box.y;
  const bottom = box.y + box.height;
  switch (corner) {
    case "tl":
      return { x: right, y: bottom };
    case "tr":
      return { x: left, y: bottom };
    case "bl":
      return { x: right, y: top };
    case "br":
      return { x: left, y: top };
  }
}

/**
 * Compute the new bounding box for a corner-resize drag.
 *
 * @param orig    The box before the drag (unrotated local frame).
 * @param corner  Which corner the user grabbed.
 * @param cursor  The cursor position in the same local frame.
 * @param opts.aspectLock  Preserve the original aspect ratio (Shift / size
 *   snap). Scales uniformly by the dominant axis so the box still covers the
 *   cursor.
 * @param opts.fromCenter  Keep the box centred on its original centre (Alt /
 *   centre snap): both the grabbed corner and its opposite move outward.
 * @param opts.minSize     Floor for width / height so the box can't collapse
 *   (default 0.1 mm).
 *
 * Returns a normalised box (non-negative width / height).
 */
export function resizeBox(
  orig: Box,
  corner: ResizeCorner,
  cursor: { x: number; y: number },
  opts: { aspectLock?: boolean; fromCenter?: boolean; minSize?: number } = {},
): Box {
  const aspectLock = opts.aspectLock ?? false;
  const fromCenter = opts.fromCenter ?? false;
  const minSize = opts.minSize ?? 0.1;
  const cx = orig.x + orig.width / 2;
  const cy = orig.y + orig.height / 2;

  if (fromCenter) {
    let halfW = Math.abs(cursor.x - cx);
    let halfH = Math.abs(cursor.y - cy);
    if (aspectLock) {
      const s = aspectScale(orig, halfW * 2, halfH * 2);
      halfW = (orig.width / 2) * s;
      halfH = (orig.height / 2) * s;
    }
    halfW = Math.max(halfW, minSize / 2);
    halfH = Math.max(halfH, minSize / 2);
    return {
      x: cx - halfW,
      y: cy - halfH,
      width: halfW * 2,
      height: halfH * 2,
    };
  }

  const anchor = oppositeCornerPoint(orig, corner);
  let w = Math.abs(cursor.x - anchor.x);
  let h = Math.abs(cursor.y - anchor.y);
  if (aspectLock) {
    const s = aspectScale(orig, w, h);
    w = orig.width * s;
    h = orig.height * s;
  }
  w = Math.max(w, minSize);
  h = Math.max(h, minSize);
  // Extend from the pinned anchor toward the side the cursor is on so the box
  // can flip past the anchor without inverting its width / height.
  const x = cursor.x >= anchor.x ? anchor.x : anchor.x - w;
  const y = cursor.y >= anchor.y ? anchor.y : anchor.y - h;
  return { x, y, width: w, height: h };
}

/**
 * The uniform scale an aspect-locked drag should apply, taken from the axes
 * that HAVE extent (#1055).
 *
 * A straight seam's bounding box is zero on one axis, and that is the commonest
 * seam there is. Dividing by `orig.height || 1` reads as a safe guard but is
 * not a ratio — it is a millimetre count, so a 20 mm wobble across the flat
 * axis scaled the other one by twenty. An 80 mm seam came out 1600 mm long.
 *
 * A degenerate axis contributes nothing instead: there is no ratio to preserve
 * along an axis with no extent, so the scale comes from the axis the maker can
 * actually drag. With neither axis (a point) the box is left to the minimum-size
 * clamp below.
 */
function aspectScale(orig: Box, w: number, h: number): number {
  const sx = orig.width > 0 ? w / orig.width : null;
  const sy = orig.height > 0 ? h / orig.height : null;
  if (sx == null && sy == null) return 1;
  return Math.max(sx ?? 0, sy ?? 0);
}

/**
 * New bounding box for an EDGE-resize drag (#863) — a single-dimension change.
 * `l` / `r` move the left / right edge (width changes, height fixed); `t` / `b`
 * move the top / bottom edge (height changes, width fixed). The opposite edge is
 * pinned, or — under `fromCenter` — the box grows symmetrically about its
 * centre. The unchanged axis keeps `orig`'s position + size. Returns a
 * normalised box (non-negative width / height); the moving edge can cross the
 * pinned one (a flip), which `resizeFlipEdge` reports so the geometry mirrors.
 */
export function resizeBoxEdge(
  orig: Box,
  edge: ResizeEdge,
  cursor: { x: number; y: number },
  opts: { fromCenter?: boolean; minSize?: number } = {},
): Box {
  const fromCenter = opts.fromCenter ?? false;
  const minSize = opts.minSize ?? 0.1;
  const left = orig.x;
  const right = orig.x + orig.width;
  const top = orig.y;
  const bottom = orig.y + orig.height;
  const cx = left + orig.width / 2;
  const cy = top + orig.height / 2;
  if (edge === "l" || edge === "r") {
    if (fromCenter) {
      const halfW = Math.max(Math.abs(cursor.x - cx), minSize / 2);
      return {
        x: cx - halfW,
        y: orig.y,
        width: halfW * 2,
        height: orig.height,
      };
    }
    const anchorX = edge === "l" ? right : left;
    const w = Math.max(Math.abs(cursor.x - anchorX), minSize);
    const x = cursor.x >= anchorX ? anchorX : anchorX - w;
    return { x, y: orig.y, width: w, height: orig.height };
  }
  if (fromCenter) {
    const halfH = Math.max(Math.abs(cursor.y - cy), minSize / 2);
    return { x: orig.x, y: cy - halfH, width: orig.width, height: halfH * 2 };
  }
  const anchorY = edge === "t" ? bottom : top;
  const h = Math.max(Math.abs(cursor.y - anchorY), minSize);
  const y = cursor.y >= anchorY ? anchorY : anchorY - h;
  return { x: orig.x, y, width: orig.width, height: h };
}

/** Which axis an EDGE-resize drag has flipped — the moving edge crossing its
 *  pinned reference (opposite edge, or centre under from-centre). Only the
 *  edge's own axis can flip; the other is always false. */
export function resizeFlipEdge(
  orig: Box,
  edge: ResizeEdge,
  cursor: { x: number; y: number },
  fromCenter = false,
): { x: boolean; y: boolean } {
  if (edge === "l" || edge === "r") {
    const grabbedX = edge === "r" ? orig.x + orig.width : orig.x;
    const refX = fromCenter
      ? orig.x + orig.width / 2
      : edge === "l"
        ? orig.x + orig.width
        : orig.x;
    return { x: (cursor.x - refX) * (grabbedX - refX) < 0, y: false };
  }
  const grabbedY = edge === "b" ? orig.y + orig.height : orig.y;
  const refY = fromCenter
    ? orig.y + orig.height / 2
    : edge === "t"
      ? orig.y + orig.height
      : orig.y;
  return { x: false, y: (cursor.y - refY) * (grabbedY - refY) < 0 };
}

/**
 * The affine that maps `orig` onto `next` (axis-aligned scale + translate):
 * `x' = sx·x + tx`, `y' = sy·y + ty`. Used to transform a line's pathData /
 * anchors so non-primitive shapes scale with their bounding box. Falls back to
 * scale 1 on a degenerate (zero-size) original axis so callers don't divide by
 * zero.
 */
export function boxToBoxTransform(
  orig: Box,
  next: Box,
): { sx: number; sy: number; tx: number; ty: number } {
  const sx = orig.width === 0 ? 1 : next.width / orig.width;
  const sy = orig.height === 0 ? 1 : next.height / orig.height;
  return {
    sx,
    sy,
    tx: next.x - sx * orig.x,
    ty: next.y - sy * orig.y,
  };
}

/**
 * Box-to-box affine that additionally reflects the content within the target
 * box on the flagged axes. Used when a corner is dragged past the pinned edge:
 * the bounding box is the same either way, but the geometry inside should
 * mirror rather than just rescale. Reflection is about the target box's centre
 * so the result still fills `next`.
 */
export function boxToBoxTransformWithFlip(
  orig: Box,
  next: Box,
  flip: { x: boolean; y: boolean },
): { sx: number; sy: number; tx: number; ty: number } {
  const t = boxToBoxTransform(orig, next);
  let { sx, sy, tx, ty } = t;
  if (flip.x) {
    const cx = next.x + next.width / 2;
    sx = -sx;
    tx = 2 * cx - tx;
  }
  if (flip.y) {
    const cy = next.y + next.height / 2;
    sy = -sy;
    ty = 2 * cy - ty;
  }
  return { sx, sy, tx, ty };
}

/**
 * Apply a box-to-box resize (with optional axis flips) to a raw pathData
 * string and return the resized pathData. When `rot` is given the transform
 * runs in the un-rotated frame — undo the tilt, scale, re-apply — matching a
 * construction line that carries baked rotation. Leather objects have no
 * persisted tilt (#971), so they pass no `rot` and the scale runs directly in
 * the axis-aligned frame. Shared by `resizeController`'s generic branch and the
 * leather `TransformController` so the scale math lives in exactly one place.
 */
export function resizePathDataByBox(
  pathData: string,
  orig: Box,
  next: Box,
  flip: { x: boolean; y: boolean },
  scope: paper.PaperScope,
  rot?: { deg: number; pivot: { x: number; y: number } },
): string {
  const t = boxToBoxTransformWithFlip(orig, next, flip);
  const path = new scope.CompoundPath({ pathData, insert: false });
  const pivot = rot ? new scope.Point(rot.pivot.x, rot.pivot.y) : null;
  if (rot && pivot) path.rotate(-rot.deg, pivot);
  path.transform(new scope.Matrix(t.sx, 0, 0, t.sy, t.tx, t.ty));
  if (rot && pivot) path.rotate(rot.deg, pivot);
  const data = path.pathData;
  path.remove();
  return data;
}

/**
 * Which axes a corner-resize drag has flipped — true when the dragged corner
 * has crossed the pinned reference (the opposite corner, or the centre when
 * resizing from centre) on that axis. Drives `boxToBoxTransformWithFlip` so the
 * geometry mirrors as the cursor passes the boundary.
 */
export function resizeFlip(
  orig: Box,
  corner: ResizeCorner,
  cursor: { x: number; y: number },
  fromCenter = false,
): { x: boolean; y: boolean } {
  const grabbed = {
    x: corner === "tr" || corner === "br" ? orig.x + orig.width : orig.x,
    y: corner === "bl" || corner === "br" ? orig.y + orig.height : orig.y,
  };
  const ref = fromCenter
    ? { x: orig.x + orig.width / 2, y: orig.y + orig.height / 2 }
    : oppositeCornerPoint(orig, corner);
  return {
    x: (cursor.x - ref.x) * (grabbed.x - ref.x) < 0,
    y: (cursor.y - ref.y) * (grabbed.y - ref.y) < 0,
  };
}

/**
 * Orientation of an axis-aligned pill (capsule) in its own unrotated frame, or
 * `null` when the axis runs diagonally (the endpoints don't share an X or Y).
 * A corner resize only maps cleanly onto a pill's length / width when the axis
 * is horizontal or vertical; diagonal capsules fall back to a generic path
 * scale. A collapsed (point) axis is treated as horizontal so a degenerate
 * pill still resizes sensibly. `eps` is in mm.
 */
export function pillOrientation(
  prim: { x1: number; y1: number; x2: number; y2: number },
  eps = 1e-3,
): "horizontal" | "vertical" | null {
  const dx = Math.abs(prim.x2 - prim.x1);
  const dy = Math.abs(prim.y2 - prim.y1);
  if (dy <= eps) return "horizontal";
  if (dx <= eps) return "vertical";
  return null;
}

/**
 * Rebuild an axis-aligned pill's endpoints + width so the capsule fills `box`.
 * The short-axis diameter is the box's short side; the overall capsule length
 * is the long side, and the stored axis (endpoint distance) is
 * `length − diameter`, clamped at 0 (a circle). The capsule stays centred on
 * the box centre. Used by the corner-resize commit so a pill resizes its
 * length (short-axis pinned) or scales proportionally (Shift) while staying a
 * parametric pill.
 */
export function boxToPill(
  box: Box,
  orientation: "horizontal" | "vertical",
): { x1: number; y1: number; x2: number; y2: number; width: number } {
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  if (orientation === "horizontal") {
    const width = box.height;
    const half = Math.max(0, box.width - width) / 2;
    return { x1: cx - half, y1: cy, x2: cx + half, y2: cy, width };
  }
  const width = box.width;
  const half = Math.max(0, box.height - width) / 2;
  return { x1: cx, y1: cy - half, x2: cx, y2: cy + half, width };
}
